import {
    Alert,
    Box,
    Button,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Tag,
    TagCloseButton,
    Text,
} from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import useModal from './useModal';
import { TInternationalCompanyGroup } from '../useInternationalCompanyGroup';
import { SearchIcon } from '@chakra-ui/icons';
import AutoCompleteSelect from 'components/atoms/AutoCompleteSelect';
import { ICompanyGroup } from 'types';

interface IProps {
    setIsModalOpen: TInternationalCompanyGroup['setIsModalOpen'];
    isModalOpen: TInternationalCompanyGroup['isModalOpen'];
    modalType: TInternationalCompanyGroup['modalType'];
    modalData: TInternationalCompanyGroup['modalData'];
    setModalData: TInternationalCompanyGroup['setModalData'];
    internationalCompanies: TInternationalCompanyGroup['internationalCompanies'];
    globalCompanyId: TInternationalCompanyGroup['globalCompanyId'];
    updateModalData: TInternationalCompanyGroup['updateModalData'];
    onDeleteOpen: TInternationalCompanyGroup['onDeleteOpen'];
}

const tagBoxProps = {
    height: '200px',
    mt: '10px',
    border: '1px solid',
    borderColor: 'borderGray',
    p: '10px',
};

const tagWrapperProps = {
    mb: '9px',
};

const tagProps = {
    mr: '9px',
    mb: '9px',
};

const InternationalCompanyGroupModal = ({
    setIsModalOpen,
    isModalOpen,
    internationalCompanies,
    modalType,
    modalData,
    updateModalData,
    setModalData,
    globalCompanyId,
    onDeleteOpen,
}: IProps) => {
    const {
        handleDelete,
        handleCloseModal,
        handleSubmit,
        handleChangeUserList,
        handleChangeCompany,
        handleChangeName,
        updateInitialRef,
        isUpdateInternationalCompanyGroupLoading,
        isAddInternationalCompanyGroupLoading,
    } = useModal({
        onDeleteOpen,
        setIsModalOpen,
        modalType,
        setModalData,
        modalData,
        globalCompanyId,
    });

    const companyGroupsByCompanyObj = modalData?.company_groups?.reduce(
        (acc: { [key: number]: ICompanyGroup[] }, companyGroup: ICompanyGroup) => {
            if (!acc[companyGroup.company_id]) {
                acc[companyGroup.company_id] = [];
            }
            acc[companyGroup.company_id].push(companyGroup);
            return acc;
        },
        {},
    );

    const companyGroupsByCompanyArr: ICompanyGroup[][] = Object.values(companyGroupsByCompanyObj).filter(
        (companyGroups) => companyGroups.length > 1,
    );

    const translate = useTranslation();
    return (
        <Modal
            initialFocusRef={updateInitialRef}
            isOpen={isModalOpen}
            onClose={() => {
                handleCloseModal();
            }}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                    {modalType === 'add' ? translate('icg.modal.title.add') : translate('icg.modal.title.edit')}
                </ModalHeader>
                <ModalCloseButton
                    isDisabled={
                        modalType === 'add'
                            ? isAddInternationalCompanyGroupLoading
                            : isUpdateInternationalCompanyGroupLoading
                    }
                />

                <ModalBody fontSize="sm">
                    <Text fontWeight="bold" mb="10px" fontSize="sm">
                        {translate('icg.modal.field.name')}
                    </Text>
                    <Input
                        size="sm"
                        onChange={(e) => handleChangeName(e.target.value)}
                        value={modalData.name}
                        placeholder={translate('icg.modal.field.name') as string}
                        mb="22px"
                    />
                    <Text fontWeight="bold" mb="10px" fontSize="sm">
                        {translate('icg.modal.field.international_cluster')}
                    </Text>
                    <Select
                        size="sm"
                        mb="22px"
                        onChange={(e) => {
                            const InternationalCompanyId = Number(e.target.value);
                            updateModalData({
                                id: InternationalCompanyId,
                                name:
                                    (internationalCompanies?.data?.find(
                                        (company) => company.id === InternationalCompanyId,
                                    )?.name as string) || '',
                            });
                        }}
                        value={modalData?.international_company?.id}
                    >
                        {internationalCompanies &&
                            internationalCompanies?.data?.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                    </Select>

                    <Flex minW="100%" gap="10px">
                        <Box w="50%">
                            <Text fontWeight="bold" mb="10px" fontSize="sm">
                                {translate('icg.modal.field.company_groups')}
                            </Text>
                            <AutoCompleteSelect
                                size="sm"
                                mutable={true}
                                onChange={(value) => {
                                    handleChangeCompany(value as number, 'add');
                                }}
                                value=""
                                customIcon={SearchIcon}
                                clearOnSelect={true}
                            >
                                {modalData?.available_company_groups?.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.company_name} - {option.name} - {option.company_id}
                                    </option>
                                ))}
                            </AutoCompleteSelect>

                            <Box {...tagBoxProps} overflowY="scroll">
                                {!modalData?.company_groups?.length ? (
                                    <Text
                                        fontSize="sm"
                                        color="gray.500"
                                        display="flex"
                                        width="100%"
                                        height="90%"
                                        alignItems="center"
                                        justifyContent="center"
                                        textAlign="center"
                                        userSelect="none"
                                    >
                                        {translate('icg.modal.field.users.no_user_groups')}
                                    </Text>
                                ) : (
                                    <></>
                                )}
                                {modalData?.company_groups?.map((companyGroup) => (
                                    <Tag {...tagProps}>
                                        {companyGroup.company_name} - {companyGroup.name}
                                        <TagCloseButton
                                            onClick={() => {
                                                handleChangeCompany(companyGroup.id, 'delete');
                                            }}
                                        />
                                    </Tag>
                                ))}
                            </Box>
                        </Box>
                        <Box w="50%">
                            <Text fontWeight="bold" mb="10px" fontSize="sm">
                                {translate('icg.modal.field.users')}
                            </Text>

                            <AutoCompleteSelect
                                size="sm"
                                mutable={true}
                                onChange={(value) => {
                                    handleChangeUserList(value as number, 'add');
                                }}
                                value=""
                                customIcon={SearchIcon}
                                clearOnSelect={true}
                            >
                                {modalData?.available_users?.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.username}
                                    </option>
                                ))}
                            </AutoCompleteSelect>

                            <Box {...tagBoxProps} overflowY="scroll">
                                {!modalData?.users?.length ? (
                                    <Text
                                        fontSize="sm"
                                        color="gray.500"
                                        display="flex"
                                        width="100%"
                                        height="90%"
                                        alignItems="center"
                                        justifyContent="center"
                                        userSelect="none"
                                    >
                                        {translate('icg.modal.field.users.no_users')}
                                    </Text>
                                ) : (
                                    <></>
                                )}

                                {modalData?.users?.map((user) => (
                                    <Box {...tagWrapperProps}>
                                        <Tag>
                                            {user.username}
                                            <TagCloseButton
                                                onClick={() => {
                                                    handleChangeUserList(user.id, 'delete');
                                                }}
                                            />
                                        </Tag>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Flex>
                    {companyGroupsByCompanyArr.length ? (
                        <Alert status="error" title="Belong to the same company" mt="20px" display="block">
                            {translate('icg.modal.error.same_company')} :
                            <br />
                            <Text>
                                {companyGroupsByCompanyArr.map((companyGroups, groupIndex) => (
                                    <Box key={groupIndex} mb="5px">
                                        {companyGroups
                                            .map((companyGroup, index) => (
                                                <span key={index}>
                                                    «{' '}
                                                    <Text fontWeight="bold" display="inline">
                                                        {companyGroups[index].company_name}
                                                    </Text>{' '}
                                                    - {companyGroup.name} »
                                                </span>
                                            ))
                                            .reduce((prev, curr) => (
                                                <>
                                                    {prev}, {curr}
                                                </>
                                            ))}
                                    </Box>
                                ))}
                            </Text>
                            {translate('icg.modal.error.same_company.condition')}
                        </Alert>
                    ) : (
                        ''
                    )}
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {modalType === 'update' && (
                        <Box
                            {...linkStyle}
                            color="red.500"
                            onClick={intentionalyFloatingPromiseReturn(handleDelete)}
                            // todo: prevent double click
                        >
                            {translate('icg.modal.button.delete')}
                        </Box>
                    )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={
                                modalType === 'add'
                                    ? isAddInternationalCompanyGroupLoading
                                    : isUpdateInternationalCompanyGroupLoading
                            }
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleCloseModal}
                        >
                            {translate('icg.modal.button.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleSubmit)}
                            isLoading={
                                modalType === 'add'
                                    ? isAddInternationalCompanyGroupLoading
                                    : isUpdateInternationalCompanyGroupLoading
                            }
                            size="sm"
                            colorScheme="blue"
                            bg="blueMain"
                            isDisabled={
                                !modalData.name ||
                                !modalData.international_company?.id ||
                                !!companyGroupsByCompanyArr.length
                            }
                        >
                            {translate('icg.modal.button.confirm')}
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InternationalCompanyGroupModal;
